import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useUnifiedService } from "../../utilities/contexts/UnifiedServiceContext";
import { ServiceStep } from "../../utilities/types/unified-services";
import { useAutoScrollToTop } from "../../utilities/hooks/useScrollToTop";
import { getServiceStepFlow } from "../../utilities/helpers/serviceStepMapper";
import { hasAcceptedPhotoPrivacy, acceptPhotoPrivacy } from "../../utilities/helpers/photoPrivacyHelper";
import UnifiedStepIndicator from "./UnifiedStepIndicator";
import UnifiedStepNavigation from "./UnifiedStepNavigation";
import PhotoPrivacyModal from "../shared/PhotoPrivacyModal";

import UnifiedGuideStep from "./steps/UnifiedGuideStep";
import UnifiedUploadStep from "./steps/UnifiedUploadStep";
import UnifiedFaceBlurStep from "./steps/UnifiedFaceBlurStep";
import UnifiedHeightInputStep from "./steps/UnifiedHeightInputStep";
import UnifiedProcessingStep from "./steps/UnifiedProcessingStep";
import UnifiedResultsStep from "./steps/UnifiedResultsStep";

const UnifiedService: React.FC = () => {
  const { serviceInterface, serviceConfig } = useUnifiedService();
  const { currentStep } = serviceInterface;
  const location = useLocation();

  // Detect SDK and Guest modes
  const isSDK = location.pathname.includes("/sdk/");
  const isGuest = location.pathname.includes("/guest/");

  // Photo Privacy state
  const [showPhotoPrivacy, setShowPhotoPrivacy] = useState(false);
  const [photoPrivacyAccepted, setPhotoPrivacyAccepted] = useState(false);

  // Check photo privacy before upload step
  useEffect(() => {
    if (currentStep === ServiceStep.UPLOAD) {
      const accepted = hasAcceptedPhotoPrivacy(isSDK, isGuest);
      setPhotoPrivacyAccepted(accepted);
      if (!accepted) {
        setShowPhotoPrivacy(true);
      }
    }
  }, [currentStep, isSDK, isGuest]);

  const handlePhotoPrivacyAccept = () => {
    acceptPhotoPrivacy(isSDK, isGuest);
    setPhotoPrivacyAccepted(true);
    setShowPhotoPrivacy(false);
  };

  useAutoScrollToTop(currentStep);

  const stepFlow = getServiceStepFlow(serviceConfig);

  const renderCurrentStep = () => {
    if (!stepFlow.isValidStep(currentStep)) {
      return <UnifiedGuideStep />;
    }

    switch (currentStep) {
      case ServiceStep.GUIDE:
        return <UnifiedGuideStep />;
      case ServiceStep.UPLOAD:
        return <UnifiedUploadStep />;
      case ServiceStep.FACE_BLUR:
        if (serviceConfig.processing.requiresFaceBlur) {
          return <UnifiedFaceBlurStep />;
        }

        return <UnifiedProcessingStep />;
      case ServiceStep.HEIGHT_INPUT:
        if (serviceConfig.configuration?.requiresHeight) {
          return <UnifiedHeightInputStep />;
        }

        return <UnifiedProcessingStep />;
      case ServiceStep.PROCESSING:
        return <UnifiedProcessingStep />;
      case ServiceStep.RESULTS:
        return <UnifiedResultsStep />;
      default:
        return <UnifiedGuideStep />;
    }
  };

  return (
    <>
      {/* Photo Privacy Modal - Shows before upload step */}
      <PhotoPrivacyModal visible={showPhotoPrivacy && !photoPrivacyAccepted} onAccept={handlePhotoPrivacyAccept} />

      <div className="min-h-dvh" style={{ backgroundColor: "#f5f5f5" }}>
        <div className="p-2 mx-auto md:p-4">
          <div className="w-full px-2 py-4 mx-auto bg-white lg:p-6 rounded-3xl">
            <UnifiedStepIndicator />
            <div className="min-h-fit">{renderCurrentStep()}</div>
            {currentStep !== ServiceStep.PROCESSING && currentStep !== ServiceStep.RESULTS && currentStep !== ServiceStep.FACE_BLUR && <UnifiedStepNavigation />}
          </div>
        </div>
      </div>
    </>
  );
};

export default UnifiedService;
